import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import OrderIdentifierInput from './components/OrderIdentifierInput'
import PayloadTable from './components/PayloadTable'
import EvidencePanel from './components/EvidencePanel'
import AddedLines from './components/AddedLines'
import CandidatesTable from './components/CandidatesTable'
import NfeStatusPanel from './components/NfeStatusPanel'
import { previewPickingConfirmation, resendPickingConfirmation } from '../../../services/naturaPickingResendService'
import { fetchNfeStatus } from '../../../services/naturaNfeStatusService'
import { formatTimestamp } from '../../../components/NaturaOps/formatTimestamp'
import {
  ERRORS_WITH_RAW_DETAIL,
  ERROR_AMBIGUOUS_ORDER_NUMBER,
  ERROR_RESEND_IN_PROGRESS,
  KNOWN_REJECTIONS,
  MODE_ORDER_ID,
  MODE_ORDER_NUMBER,
  NATURA_OUTCOME_ERRORS,
  groupByDelivItem,
  mergeCorrections,
  padDelivItem,
} from './constants'

const STATE_IDLE = 'idle'
const STATE_PREVIEWING = 'previewing'
const STATE_PREVIEW_READY = 'preview_ready'
const STATE_RESENDING = 'resending'
const STATE_DONE = 'done'

const FAILURE_NONE = null
const FAILURE_REJECTION = 'rejection'
const FAILURE_GENERIC = 'generic'
// A resend that fails without a body may still have reached SAP, so it cannot share
// the "nothing happened, try again" panel a failed preview uses.
const FAILURE_UNKNOWN_DELIVERY = 'unknown_delivery'

// A 502 body is only renderable as a result if it actually carries the round trip;
// without it there is nothing to show and it falls back to the generic panel.
const classify = error => {
  const data = error?.response?.data
  const code = data?.error

  if (NATURA_OUTCOME_ERRORS.includes(code) && data.request) return { kind: 'outcome', data }
  if (KNOWN_REJECTIONS.includes(code)) return { kind: FAILURE_REJECTION, data }
  return { kind: FAILURE_GENERIC }
}

export default function PickingPage() {
  const { t } = useTranslation()
  const key = suffix => `natura_operations.picking.${suffix}`

  const [mode, setMode] = useState(MODE_ORDER_NUMBER)
  const [identifier, setIdentifier] = useState('')

  const [state, setState] = useState(STATE_IDLE)
  const [preview, setPreview] = useState(null)
  const [outcome, setOutcome] = useState(null)
  const [excluded, setExcluded] = useState([])
  const [added, setAdded] = useState([])

  const [failureKind, setFailureKind] = useState(FAILURE_NONE)
  const [rejection, setRejection] = useState(null)

  const [nfeStatus, setNfeStatus] = useState(null)
  const [nfeLoading, setNfeLoading] = useState(false)

  const isBusy = state === STATE_PREVIEWING || state === STATE_RESENDING

  const groups = groupByDelivItem(preview?.entries)
  // A corrected line is still sent, only with edited values, so a line counts as gone from the
  // payload only when its removal has no correction behind it. Several rows can correct one
  // line — one per lot — and that is still one line.
  const correctedDelivItems = [...new Set(added.map(line => padDelivItem(line.deliv_item)))]
  const removedDelivItems = excluded.filter(item => !correctedDelivItems.includes(item))
  const remaining = groups.length - removedDelivItems.length
  // The platform refuses an empty payload too; blocking it here keeps the operator
  // from spending a round trip to learn it.
  const wouldBeEmpty = groups.length > 0 && remaining <= 0
  // The platform accepts an added line only when this same request removes it, so a correction is
  // always paired with its removal. A line the payload still has is refused server-side.
  const isPaired = line => excluded.includes(padDelivItem(line.deliv_item))
  const unpairedLines = added.filter(line => !isPaired(line)).map(line => padDelivItem(line.deliv_item))

  // Any edit to the identifier invalidates the payload on screen, so a preview built
  // for one order can never be resent against another.
  const invalidate = () => {
    setPreview(null)
    setOutcome(null)
    setExcluded([])
    setAdded([])
    setFailureKind(FAILURE_NONE)
    setRejection(null)
    setNfeStatus(null)
    setState(STATE_IDLE)
  }

  // Keeps the outcome: it records a POST that already happened, so unlike a preview it
  // cannot go stale, and one keystroke must not destroy the evidence.
  const discardPreview = () => {
    setPreview(null)
    setExcluded([])
    setAdded([])
    setFailureKind(FAILURE_NONE)
    setRejection(null)
    setState(STATE_IDLE)
  }

  const handleModeChange = next => {
    setMode(next)
    setIdentifier('')
    invalidate()
  }

  const handleIdentifierChange = value => {
    setIdentifier(value)
    discardPreview()
  }

  const selectCandidate = candidateId => {
    setMode(MODE_ORDER_ID)
    setIdentifier(candidateId)
    invalidate()
  }

  const toggleLine = delivItem => {
    setExcluded(current => (current.includes(delivItem) ? current.filter(item => item !== delivItem) : [...current, delivItem]))
  }

  // Correcting a line is remove-and-re-add in one action: the row is prefilled from what the
  // payload says so the operator edits only the field that was wrong.
  const correctLine = delivItem => {
    const group = groups.find(candidate => candidate.delivItem === delivItem)
    if (!group) return

    // Removal is per line number, so it drops every lot reported on it. One row per lot
    // puts the same quantity back — seeding from the first lot alone would under-ship.
    const sources = group.entries.length > 0 ? group.entries : [{}]
    setAdded(current => {
      // Correcting the same line twice would confirm it twice: the platform dedupes whole
      // entries, so two rows differing by one edited field both reach SAP.
      if (current.some(line => padDelivItem(line.deliv_item) === delivItem)) return current
      let id = current.reduce((max, line) => Math.max(max, line.id), 0)
      return [
        ...current,
        ...sources.map(source => ({
          id: ++id,
          deliv_item: delivItem,
          sku: source.material_display || '',
          batch: source.batch || '',
          dlv_qty: String(source.dlv_qty || ''),
          source_batch: source.batch || '',
        })),
      ]
    })
    setExcluded(current => (current.includes(delivItem) ? current : [...current, delivItem]))
  }

  const updateAdded = (id, patch) => {
    setAdded(current => current.map(line => (line.id === id ? { ...line, ...patch } : line)))
  }

  // Discarding a correction is an undo: the line was only removed to make room for the row
  // that is going away, so it goes back — unless another lot of it is still corrected.
  const removeAdded = id => {
    const target = added.find(line => line.id === id)
    const next = added.filter(line => line.id !== id)
    setAdded(next)
    if (!target) return

    const delivItem = padDelivItem(target.deliv_item)
    if (!next.some(line => padDelivItem(line.deliv_item) === delivItem)) {
      setExcluded(current => current.filter(item => item !== delivItem))
    }
  }

  // `sent` distinguishes the two callers: a preview that fails sent nothing, while a
  // resend that fails without a body may have reached SAP and must say so.
  const handleFailure = (error, { sent } = {}) => {
    const classified = classify(error)
    if (classified.kind === 'outcome') {
      setOutcome(classified.data)
      setFailureKind(FAILURE_NONE)
      return true
    }
    if (classified.kind === FAILURE_REJECTION) {
      setRejection(classified.data)
      setFailureKind(FAILURE_REJECTION)
      return false
    }
    setFailureKind(sent ? FAILURE_UNKNOWN_DELIVERY : FAILURE_GENERIC)
    return false
  }

  const generatePreview = async () => {
    if (isBusy || identifier.trim() === '') return
    setState(STATE_PREVIEWING)
    setPreview(null)
    setOutcome(null)
    setExcluded([])
    // Corrections are edits to a payload that is about to be replaced. Keeping them would
    // carry a quantity read off the old payload into whatever the rebuild returns.
    setAdded([])
    setFailureKind(FAILURE_NONE)
    setRejection(null)
    setNfeStatus(null)
    try {
      const data = await previewPickingConfirmation({ [mode]: identifier.trim() })
      setPreview(data)
      setState(STATE_PREVIEW_READY)
    } catch (e) {
      handleFailure(e)
      setState(STATE_IDLE)
    }
  }

  const resend = async () => {
    if (!preview || isBusy || wouldBeEmpty || unpairedLines.length > 0) return
    setState(STATE_RESENDING)
    setOutcome(null)
    setFailureKind(FAILURE_NONE)
    setRejection(null)
    try {
      // Sent by resolved id and with the preview's digest: the server rebuilds from live
      // stock, and without the digest it could post a payload the operator never read.
      const data = await resendPickingConfirmation({
        orderId: preview.order_id,
        excludedDelivItems: excluded,
        addedEntries: mergeCorrections(added),
        expectedDigest: preview.digest,
      })
      setOutcome(data)
      setState(STATE_DONE)
    } catch (e) {
      const rendered = handleFailure(e, { sent: true })
      setState(rendered ? STATE_DONE : STATE_PREVIEW_READY)
    }
  }

  const checkNfeStatus = async () => {
    const orderNumber = preview?.order_number || outcome?.order_number
    if (!orderNumber || nfeLoading) return
    setNfeLoading(true)
    setNfeStatus(null)
    try {
      const data = await fetchNfeStatus([orderNumber])
      setNfeStatus(data?.results?.[0] || { outcome: 'errored' })
    } catch (e) {
      setNfeStatus({ outcome: 'errored' })
    } finally {
      setNfeLoading(false)
    }
  }

  const resendLabel = () => {
    if (state === STATE_RESENDING) return t(key('resending'))
    const corrections = correctedDelivItems.length
    const removals = removedDelivItems.length
    if (corrections > 0 && removals > 0) return t(key('resend_with_corrections_and_removals'), { corrections, lines: removals })
    if (corrections > 0) return t(key('resend_with_corrections'), { lines: corrections })
    if (removals > 0) return t(key('resend_without_lines'), { lines: removals })
    return t(key('resend'))
  }

  const rejectionHeading = () => t(key(`rejection_${rejection.error}`))
  const showRawDetail = ERRORS_WITH_RAW_DETAIL.includes(rejection?.error) && rejection?.message
  const canCheckNfe = Boolean(preview?.order_number || outcome?.order_number)
  // Only sends that reached the POST stage are reported, so any entry means SAP has had
  // this delivery. A warning, never a block: a resend on top of a success is sometimes the fix.
  const priorSends = preview?.prior_sends || []

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">{t(key('title'))}</h1>

      <section className="bg-gray-50 border border-gray-200 rounded p-4 mb-6">
        <h2 className="font-semibold mb-2">{t(key('instructions_heading'))}</h2>
        <ol className="list-decimal pl-6 text-sm space-y-1">
          <li>{t(key('instructions_step1'))}</li>
          <li>{t(key('instructions_step2'))}</li>
          <li>{t(key('instructions_step3'))}</li>
          <li>{t(key('instructions_step4'))}</li>
          <li>{t(key('instructions_step5'))}</li>
        </ol>
        <p className="mt-3 text-sm text-amber-700">{t(key('warning'))}</p>
      </section>

      {failureKind === FAILURE_REJECTION && rejection && (
        <div role="alert" className="mb-4 p-3 border border-red-200 bg-red-50 rounded text-sm text-red-800">
          <p>{rejectionHeading()}</p>
          {rejection.error === ERROR_RESEND_IN_PROGRESS && rejection.retry_after_seconds && (
            <p className="mt-1">{t(key('retry_after'), { seconds: rejection.retry_after_seconds })}</p>
          )}
          {showRawDetail && <pre className="mt-2 text-xs whitespace-pre-wrap break-words max-h-60 overflow-auto">{rejection.message}</pre>}
          {rejection.error === ERROR_AMBIGUOUS_ORDER_NUMBER && (rejection.candidates || []).length > 0 && (
            <CandidatesTable candidates={rejection.candidates} onSelect={selectCandidate} />
          )}
        </div>
      )}

      <section className="mb-6">
        <OrderIdentifierInput mode={mode} value={identifier} onModeChange={handleModeChange} onValueChange={handleIdentifierChange} disabled={isBusy} />
      </section>

      <div>
        <button
          type="button"
          onClick={generatePreview}
          disabled={isBusy || identifier.trim() === ''}
          className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white"
        >
          {state === STATE_PREVIEWING ? t(key('generating_preview')) : t(key('generate_preview'))}
        </button>
      </div>

      {failureKind === FAILURE_GENERIC && (
        <div role="alert" className="mt-6 p-4 border border-red-200 bg-red-50 rounded text-sm text-red-800">
          <p className="mb-2">{t(key('error_generic'))}</p>
          <button type="button" onClick={generatePreview} className="px-3 py-1.5 text-sm rounded bg-red-600 hover:bg-red-700 text-white">
            {t(key('retry'))}
          </button>
        </div>
      )}

      {failureKind === FAILURE_UNKNOWN_DELIVERY && (
        <div role="alert" className="mt-6 p-4 border border-amber-200 bg-amber-50 rounded text-sm text-amber-800">
          <p className="font-medium">{t(key('delivery_unknown'))}</p>
          <p className="mt-1">{t(key('delivery_unknown_explanation'))}</p>
          <p className="mt-2">{t(key('unknown_delivery_detail'))}</p>
        </div>
      )}

      {preview && !outcome && (
        <>
          <EvidencePanel data={preview} />

          <section className="mt-6">
            <h2 className="font-semibold mb-1">{t(key('lines_heading'))}</h2>
            <p className="text-sm text-gray-600 mb-3">{t(key('lines_explanation'))}</p>
            <PayloadTable entries={preview.entries || []} excluded={excluded} onToggle={toggleLine} onCorrect={correctLine} disabled={isBusy} />
            <p className="mt-2 text-sm text-gray-700">{t(key('lines_summary'), { remaining, total: groups.length })}</p>
            {wouldBeEmpty && (
              <p role="alert" className="mt-2 text-sm text-red-700">
                {t(key('rejection_empty_payload'))}
              </p>
            )}
          </section>

          {added.length > 0 && (
            <AddedLines lines={added} onChange={updateAdded} onRemove={removeAdded} isUnpaired={line => !isPaired(line)} disabled={isBusy} />
          )}

          {unpairedLines.length > 0 && (
            <div role="alert" className="mt-3 p-3 border border-red-200 bg-red-50 rounded text-sm text-red-800">
              <p className="font-medium">{t(key('unpaired_heading'), { lines: unpairedLines.join(', ') })}</p>
              <p className="mt-1">{t(key('unpaired_explanation'))}</p>
            </div>
          )}

          {preview.qa_store && (
            <div role="status" className="mt-3 p-3 border border-amber-200 bg-amber-50 rounded text-sm text-amber-800">
              {t(key('qa_store_notice'))}
            </div>
          )}

          {priorSends.length > 0 && (
            <div role="alert" className="mt-3 p-3 border border-amber-300 bg-amber-50 rounded text-sm text-amber-900">
              <p className="font-medium">{t(key('prior_sends_warning'), { count: priorSends.length })}</p>
              <p className="mt-1">{t(key('prior_sends_last'), { at: formatTimestamp(priorSends[priorSends.length - 1].at) })}</p>
              <p className="mt-1">{t(key('prior_sends_explanation'))}</p>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={resend}
              disabled={isBusy || preview.qa_store || wouldBeEmpty || unpairedLines.length > 0}
              className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white"
            >
              {resendLabel()}
            </button>
            <button type="button" onClick={invalidate} disabled={isBusy} className="px-4 py-2 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50">
              {t(key('cancel'))}
            </button>
          </div>
        </>
      )}

      {outcome && <EvidencePanel data={outcome} showOutcome />}

      {(preview || outcome) && canCheckNfe && (
        <section className="mt-6">
          <button
            type="button"
            onClick={checkNfeStatus}
            disabled={nfeLoading}
            className="px-4 py-2 rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {nfeLoading ? t(key('checking_nfe_status')) : t(key('check_nfe_status'))}
          </button>
          <NfeStatusPanel result={nfeStatus} />
        </section>
      )}
    </div>
  )
}
